/**
 * Wrapper hook for useCurrentUser that returns mock data in demo mode.
 *
 * In demo mode (when auth is bypassed), this returns mock user data
 * instead of making an API call to /api/v1/me.
 */
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  useQuery,
  type DataTag,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCurrentUser as useGeneratedCurrentUser } from '@/generated/hooks/agora/v1/user';
import { isE2ETestMode } from '../utils/e2e';
import type {
  RpcStatus,
  V1CurrentUserResponse,
} from '@/generated/hooks/agora/models';

const MOCK_USER_RESPONSE: AxiosResponse<V1CurrentUserResponse> = {
  data: {
    user: {
      name: 'users/e2e-test-user-id',
      display_name: 'Sam B',
      email: 'sam.b@acmecorp.com',
      provider_id: 'e2e-test-provider-id-12345',
      create_time: '2026-01-06T17:06:24.065916Z',
      update_time: '2026-01-06T17:06:24.065916Z',
      onboarding_state: 'COMPLETED',
      last_org: 'e2e-test-org-id',
      last_workspace: 'e2e-test-workspace-id',
    },
  },
  status: 200,
  statusText: 'OK',
  headers: {},
  config: {} as AxiosRequestConfig,
} as AxiosResponse<V1CurrentUserResponse>;

/**
 * Hook to get current user data.
 * Returns mock data in demo mode, otherwise uses the generated hook.
 */
export function useCurrentUser<
  TData = AxiosResponse<V1CurrentUserResponse>,
  TError = AxiosError<RpcStatus>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<AxiosResponse<V1CurrentUserResponse>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const isDemoMode = isE2ETestMode();
  const isEnabled = options?.query?.enabled !== false;

  // Mock query is always declared to satisfy Rules of Hooks; disabled when not in demo mode.
  const mockQueryOptions = {
    queryKey: ['/api/v1/me'] as const,
    queryFn: async () => MOCK_USER_RESPONSE,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...options?.query,
    enabled: isDemoMode && isEnabled,
  } as UseQueryOptions<AxiosResponse<V1CurrentUserResponse>, TError, TData>;

  const mockQuery = useQuery(mockQueryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  >;

  // Generated query is also always declared; disabled when in demo mode.
  const generatedQuery = useGeneratedCurrentUser(
    {
      ...options,
      query: {
        ...options?.query,
        enabled: !isDemoMode && isEnabled,
      },
    },
    queryClient,
  );

  if (isDemoMode) {
    return Object.assign(mockQuery, {
      queryKey: mockQueryOptions.queryKey as DataTag<QueryKey, TData, TError>,
    });
  }

  return generatedQuery;
}
